import React from 'react';
import { useModels } from '../contexts/ModelContext';
import { useAuth } from '../contexts/AuthContext';
import ComplianceGauge from './ComplianceGauge';
import ModelRegistry from './ModelRegistry';
import OnboardForm from './OnboardForm';
import DemoTour from './DemoTour';
import ExecutiveDossier from './ExecutiveDossier';
import { 
  ExclamationTriangleIcon, 
  ShieldCheckIcon, 
  BoltIcon, 
  CpuChipIcon, 
  DocumentArrowDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

function Dashboard() {
  const { models, getComplianceStats, loading, loadModels } = useModels();
  const { userProfile } = useAuth();
  const [showTour, setShowTour] = React.useState(false);
  const [showDossier, setShowDossier] = React.useState(false);

  const stats = getComplianceStats();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="text-sm font-medium text-gray-600">Synchronizing AI Governance Matrix...</span>
        </div>
      </div>
    );
  }

  const getRoleBasedWelcome = () => {
    switch (userProfile?.role) {
      case 'Executive':
        return {
          title: 'Executive AI Governance & Regulatory Oversight',
          subtitle: 'Continuous compliance posture across EU AI Act (2024/1689), Colorado SB 24-205, and NIST SP 1270-1'
        };
      case 'DPO':
        return {
          title: 'Data Protection Officer (DPO) & Regulatory Arbiter',
          subtitle: 'Statutory oversight: Fundamental Rights Impact Assessments (FRIA Art. 27) and algorithmic bias audits'
        };
      case 'Developer':
        return {
          title: 'AI Engineering & Foundation Model Registry',
          subtitle: 'Onboard models with automated Jev System 1 edge reflex guardrails and FLOPs systemic risk detection'
        };
      default:
        return {
          title: 'AI Governance & Compliance Platform',
          subtitle: 'Enterprise AI safety, statutory adherence, and cryptographic arbiter telemetry'
        };
    }
  };

  const welcome = getRoleBasedWelcome();

  const handleExportRegulatoryReport = () => {
    const headers = [
      'Model ID',
      'Model Name',
      'Base Architecture',
      'Use Case',
      'Jurisdiction',
      'Risk Tier',
      'Applicable Regulation',
      'Compliance Status',
      'DPO Sign-Off',
      'FRIA Art 27 Completed',
      'Colorado SB 205 Audited',
      'Jev Arbiter Sealed',
      'Jev Arbiter Hash',
      'Reflex Latency (ms)',
      'EU AI Act Score (%)',
      'Colorado SB 205 Score (%)',
      'NIST AI RMF Score (%)',
      'ISO 42001 Score (%)',
      'Last Updated'
    ];

    const rows = models.map(m => [
      `"${m.id}"`,
      `"${m.name}"`,
      `"${m.baseModel || ''}"`,
      `"${m.useCase}"`,
      `"${m.jurisdiction || 'Global'}"`,
      `"${m.riskTier}"`,
      `"${m.regulation || ''}"`,
      `"${m.complianceStatus}"`,
      `"${m.dpoSignOff ? 'Yes' : 'No'}"`,
      `"${m.friaCompleted ? 'Yes' : 'No'}"`,
      `"${m.coloradoSB205Audited ? 'Yes' : 'No'}"`,
      `"${m.jevArbiter?.sealed ? 'Yes' : 'No'}"`,
      `"${m.jevArbiter?.sealHash || 'Unsealed'}"`,
      `"${m.jevArbiter?.reflexLatencyMs || 0}"`,
      `"${m.frameworkScores?.euAiAct ?? 0}"`,
      `"${m.frameworkScores?.coloradoSb205 ?? 0}"`,
      `"${m.frameworkScores?.nistAiRmf ?? 0}"`,
      `"${m.frameworkScores?.iso42001 ?? 0}"`,
      `"${m.lastUpdated}"`
    ].join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `enterprise-ai-governance-audit-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50/70 pb-20">
      {/* 2026 AI Governance Operational Banner */}
      <div className="bg-slate-900 text-white px-4 py-2.5 text-xs sm:text-sm border-b border-slate-800 shadow-xs">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-2.5">
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-slate-200">
              <strong className="text-white font-semibold">Governance Arbiter Node Active:</strong> EU AI Act 2024/1689 &bull; Colorado SB 24-205 &bull; NIST SP 1270-1 &bull; Jev Dual-System
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadModels()}
              className="text-slate-300 hover:text-white flex items-center gap-1.5 text-xs px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-colors"
              title="Reload Governance Ledger"
            >
              <ArrowPathIcon className="h-3.5 w-3.5 text-blue-400" />
              <span>Sync Ledger</span>
            </button>
            <button
              onClick={() => setShowTour(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white px-3 py-1 rounded text-xs font-bold tracking-wide shadow-xs transition-all"
            >
              Guided Tour
            </button>
          </div>
        </div>
      </div>

      {/* Critical Article 5 Prohibited Alert */}
      {stats.prohibitedCount > 0 && (
        <div className="bg-gradient-to-r from-rose-600 to-red-600 text-white px-4 py-3 shadow-md border-b border-red-700">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-white/20 rounded-lg flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-extrabold text-sm tracking-wide uppercase">
                  EU AI Act Article 5 Prohibited AI Detected:
                </span>
                <span className="text-sm ml-2 text-rose-100">
                  {stats.prohibitedCount} registered system(s) violate strictly prohibited practices. Blocked by Jev Arbiter to shield enterprise from €35M statutory liability.
                </span>
              </div>
            </div>
            <a
              href="#model-registry"
              className="text-xs bg-white text-rose-700 px-3.5 py-1.5 rounded-lg font-bold hover:bg-rose-50 uppercase tracking-wider shadow-xs transition-colors flex-shrink-0"
            >
              Inspect & Decommission
            </a>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="border-b border-slate-200/80 pb-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-blue-50 text-blue-700 border border-blue-200/80">
                  <ShieldCheckIcon className="w-3.5 h-3.5" />
                  {userProfile?.role || 'Executive'} Perspective
                </span>
                <span className="text-xs text-slate-400">&bull;</span>
                <span className="text-xs text-slate-500 font-medium">Continuous Telemetry Active</span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
                {welcome.title}
              </h1>
              <p className="mt-1.5 text-xs sm:text-sm text-slate-600 max-w-3xl leading-relaxed">
                {welcome.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowDossier(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-md shadow-blue-500/20 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
              >
                <ShieldCheckIcon className="h-4 w-4" />
                Board Audit Dossier (PDF)
              </button>
              <button
                onClick={handleExportRegulatoryReport}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-slate-700 bg-white border border-slate-200/90 shadow-xs hover:bg-slate-50 hover:text-slate-900 transition-all transform hover:-translate-y-0.5"
              >
                <DocumentArrowDownIcon className="h-4 w-4 text-slate-500" />
                Export Ledger (CSV)
              </button>
            </div>
          </div>
        </div>

        {/* Top Regulatory KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 mb-8">
          {/* Card 1: Total Systems */}
          <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-5 relative overflow-hidden card-lift">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total AI Systems</div>
            <div className="mt-2 text-3xl font-black text-slate-900 tracking-tight">{stats.total}</div>
            <div className="mt-2 flex items-center gap-1.5 text-xs">
              <span className="font-semibold text-emerald-600">{stats.passed} Passed</span>
              <span className="text-slate-300">/</span>
              <span className="font-semibold text-rose-600">{stats.failed} Action</span>
            </div>
          </div>

          {/* Card 2: Article 5 Prohibited */}
          <div className={`rounded-2xl shadow-xs border p-5 relative overflow-hidden card-lift ${
            stats.prohibitedCount > 0 
              ? 'bg-rose-50/50 border-rose-200' 
              : 'bg-white border-slate-200/80'
          }`}>
            <div className={`absolute top-0 left-0 right-0 h-1 ${stats.prohibitedCount > 0 ? 'bg-rose-600' : 'bg-slate-300'}`}></div>
            <div className="text-[11px] font-bold text-rose-700 uppercase tracking-wider flex items-center justify-between">
              <span>EU Art. 5 Banned</span>
              {stats.prohibitedCount > 0 && (
                <span className="h-2 w-2 rounded-full bg-rose-600 animate-ping"></span>
              )}
            </div>
            <div className={`mt-2 text-3xl font-black tracking-tight ${stats.prohibitedCount > 0 ? 'text-rose-600' : 'text-slate-900'}`}>
              {stats.prohibitedCount}
            </div>
            <div className="mt-2 text-xs font-semibold text-rose-600">
              {stats.prohibitedCount > 0 ? '€35M Exposure Risk' : '0 Prohibited Systems'}
            </div>
          </div>

          {/* Card 3: Annex III High-Risk */}
          <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-5 relative overflow-hidden card-lift">
            <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500"></div>
            <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">Annex III High-Risk</div>
            <div className="mt-2 text-3xl font-black text-slate-900 tracking-tight">{stats.highRiskCount}</div>
            <div className="mt-2 text-xs text-slate-500 font-medium">Colorado Consequential</div>
          </div>

          {/* Card 4: GPAI Systemic */}
          <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-5 relative overflow-hidden card-lift">
            <div className="absolute top-0 left-0 right-0 h-1 bg-purple-500"></div>
            <div className="text-[11px] font-bold text-purple-700 uppercase tracking-wider flex items-center gap-1">
              <CpuChipIcon className="w-3.5 h-3.5 text-purple-600" />
              GPAI Systemic
            </div>
            <div className="mt-2 text-3xl font-black text-purple-900 tracking-tight">{stats.gpaiCount}</div>
            <div className="mt-2 text-xs text-purple-700 font-semibold">&ge; 10²⁵ FLOPs Frontier</div>
          </div>

          {/* Card 5: Jev Sealed */}
          <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-5 relative overflow-hidden card-lift">
            <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500"></div>
            <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
              <BoltIcon className="w-3.5 h-3.5 text-emerald-600" />
              Jev Arbiter Sealed
            </div>
            <div className="mt-2 text-3xl font-black text-emerald-600 tracking-tight">{stats.jevSealedCount}</div>
            <div className="mt-2 text-xs text-emerald-700 font-semibold">&lt; 1ms Edge Guardrails</div>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Compliance Matrix & Radial Gauge - 2 columns */}
          <div className="lg:col-span-2" id="compliance-gauge">
            <ComplianceGauge 
              complianceRate={stats.complianceRate} 
              stats={stats}
            />
          </div>

          {/* Quick Regulatory Guidance & Role Context */}
          <div className="space-y-4" id="role-actions">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">
                Governance Health Summary
              </h3>
              <div className="space-y-2.5 text-xs">
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Fundamental Rights Impact (FRIA):</span>
                  <span className="font-semibold text-indigo-700">
                    {stats.friaCompleted} of {stats.highRiskCount} Complete
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">DPO & Legal Sign-Offs:</span>
                  <span className="font-semibold text-blue-700">
                    {stats.dpoApproved} of {stats.total} Approved
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <span className="text-gray-600">Jev Injection Shield Active:</span>
                  <span className="font-semibold text-emerald-700">
                    {stats.jevShieldActiveCount} of {stats.total} Verified
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-gray-800 font-medium">Awaiting Statutory Review:</span>
                  <span className="font-bold text-amber-700">
                    {stats.pending + stats.failed} Models
                  </span>
                </div>
              </div>
            </div>

            {/* Role-specific Action Guidance */}
            {userProfile?.role === 'Developer' && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <BoltIcon className="w-4 h-4 text-blue-600" />
                  Engineering Guidelines
                </h4>
                <ul className="text-xs text-blue-800 space-y-1.5">
                  <li>&bull; Declare model compute (FLOPs) to evaluate GPAI systemic thresholds</li>
                  <li>&bull; Integrate Jev System 1 Reflex for sub-millisecond OWASP LLM01 defense</li>
                  <li>&bull; Ensure Zero-Knowledge PII Tokenization is active before production deployment</li>
                  <li>&bull; Complete Article 11 technical documentation cards</li>
                </ul>
              </div>
            )}

            {userProfile?.role === 'DPO' && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
                <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <ShieldCheckIcon className="w-4 h-4 text-purple-600" />
                  DPO & Compliance Officer Workflow
                </h4>
                <ul className="text-xs text-purple-800 space-y-1.5">
                  <li>&bull; Verify Article 27 FRIA for high-risk credit, hiring, and medical models</li>
                  <li>&bull; Audit Colorado SB 24-205 algorithmic discrimination reports</li>
                  <li>&bull; Review cryptographic Jev Arbiter seals before signing off on deployment</li>
                  <li>&bull; Immediately issue decommissioning orders for Article 5 violations</li>
                </ul>
              </div>
            )}

            {userProfile?.role === 'Executive' && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                <h4 className="text-xs font-bold text-emerald-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-600" />
                  Board-Level AI Risk Management
                </h4>
                <ul className="text-xs text-emerald-800 space-y-1.5">
                  <li>&bull; Protect enterprise from statutory liability (€35M / 7% turnover)</li>
                  <li>&bull; Monitor high-risk consequential decision systems in Colorado & EU</li>
                  <li>&bull; Export the cryptographic audit ledger for external board review</li>
                </ul>
                <div className="mt-3">
                  <button 
                    onClick={handleExportRegulatoryReport}
                    className="w-full text-xs font-semibold bg-emerald-600 text-white py-2 px-3 rounded-lg hover:bg-emerald-700 shadow-sm transition-colors text-center"
                  >
                    Download Executive Audit Ledger
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Onboard Form - Developers */}
        {userProfile?.role === 'Developer' && (
          <div className="mb-8" id="onboard-form">
            <OnboardForm />
          </div>
        )}

        {/* Model Registry */}
        <div id="model-registry">
          <ModelRegistry />
        </div>
      </div>

      {/* Demo Tour */}
      <DemoTour isOpen={showTour} onClose={() => setShowTour(false)} />

      {/* Board-Ready Regulatory Audit Dossier Modal */}
      <ExecutiveDossier 
        isOpen={showDossier} 
        onClose={() => setShowDossier(false)} 
        stats={stats} 
        models={models} 
      />
    </div>
  );
}

export default Dashboard;